import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { doc, setDoc, getDoc, getFirestore } from "firebase/firestore";
import app, { auth, database } from "../firebaseConfig";
import { ref as dbRef, set as dbSet } from "firebase/database";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal";

const titleCase = (input = "") =>
  input
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
const COMPANY_TRIAL_DURATION_DAYS = 30;

export default function SignupPage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // get Firestore instance from the initialized app
  const firestore = getFirestore(app);

  const checkCompanyExists = async (companyKey) => {
    const docSnap = await getDoc(doc(firestore, "companies", companyKey));
    return docSnap.exists();
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (!fullName || !email || !password || !accountType) {
        setError("Please fill all required fields and pick an account type.");
        setLoading(false);
        return;
      }

      if (accountType === "Company" && !companyName.trim()) {
        setError("Please enter a company name for company registration.");
        setLoading(false);
        return;
      }

      if (accountType === "Company" && !termsAccepted) {
        setError("You must accept the Terms and Conditions to create a company account.");
        setLoading(false);
        return;
      }

      const normalizedCompanyName = titleCase(companyName || "");
      const companyKey = normalizedCompanyName.replace(/\//g, "-");

      if (accountType === "Company") {
        const exists = await checkCompanyExists(companyKey);
        if (exists) {
          setError(`A company named "${normalizedCompanyName}" already exists.`);
          setLoading(false);
          return;
        }
      }

      const methods = await fetchSignInMethodsForEmail(auth, email).catch(() => []);
      if (methods && methods.length > 0) {
        setError("An account with this email already exists.");
        setLoading(false);
        return;
      }

      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = cred.user.uid;
      const timestamp = Date.now();

      let normalizedAccountType = accountType.trim();

      // Map "MilkSeller" to "client"
      if (normalizedAccountType.toLowerCase() === "milkseller") {
        normalizedAccountType = "client";
      }

      const userData = {
        uid,
        fullName: titleCase(fullName),
        email,
        accountType: normalizedAccountType,
        companyName: normalizedCompanyName || null,
        registrationNumber: registrationNumber || null,
        timestamp,
        // For employees, we'll store pending status until company admin approves
        ...(accountType === "Employee" && {
          employeeStatus: "pending",
          requestedCompany: null,
        }),
      };

      // users collection
      await setDoc(doc(firestore, "users", uid), userData);

      // If creating a company, create company doc + employee record
      if (accountType === "Company") {
        const trialExpiryDate = new Date(
          timestamp + COMPANY_TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000
        )
          .toISOString()
          .split("T")[0];
        const companyInfo = {
          companyName: normalizedCompanyName,
          registrationNumber: registrationNumber || null,
          createdAt: timestamp,
          adminUid: uid,
          // optional meta flag showing accounts initialized
          accountsMeta: { initialized: true },
          subscriptionStatus: "active",
          subscriptionStatusReason: "",
          subscriptionExpiresAt: trialExpiryDate,
          status: "active",
          status_reason: "",
          subscription_expires_at: trialExpiryDate,
          subscription_active: true,
          can_operate: true,
        };

        await setDoc(doc(firestore, "companies", companyKey), companyInfo);
        await setDoc(
          doc(firestore, "companies", companyKey, "subscriptionSnapshots", "current"),
          {
            ...companyInfo,
            snapshotAt: timestamp,
          },
          { merge: true }
        );

        const employeeData = {
          uid,
          fullName: titleCase(fullName),
          email,
          role: "CompanyAdmin",
          department: "Management",
          permissions: {
            canAddPayments: true,
            canEditRecords: true,
            canApprovePayments: true,
            canViewReports: true,
          },
          timestamp,
        };

        // create a document under companies/{companyKey}/employees/{uid}
        await setDoc(doc(firestore, "companies", companyKey, "employees", uid), employeeData);

        // Initialize default account balances in Realtime Database (zeroed)
        // Keeps balances node for fast reads in AccountsOverview
        try {
          const ACCOUNT_TYPES = [
            "farmer_payable",
            "farmer_advance",
            "liabilities",
            "company_cash",
            "company_bank",
            "shop_receivable",
          ];

          const now = new Date();
          const initDataPromises = ACCOUNT_TYPES.map((acct) => {
            const path = `Companies/${companyKey}/AccountBalances/${acct}`;
            const payload = { balance: 0, updatedAt: timestamp };
            return dbSet(dbRef(database, path), payload);
          });

          // Optionally create an empty current-month folder for Accounts (not required)
          // but helpful if UI expects the month node to exist.
          const year = String(now.getFullYear());
          const month = String(now.getMonth() + 1);
          const emptyAccountsPromises = ACCOUNT_TYPES.map((acct) => {
            const path = `Companies/${companyKey}/Accounts/${acct}/${year}/${month}`;
            // set an empty object or leave absent; here we ensure the node exists (empty)
            return dbSet(dbRef(database, path), {});
          });

          await Promise.all([...initDataPromises, ...emptyAccountsPromises]);
        } catch (err) {
          // non-fatal: log but don't block signup
          console.error("Failed to initialize account balances:", err);
        }
      } else if (accountType === "Farmer") {
        await setDoc(doc(firestore, "farmers", uid), {
          uid,
          fullName: titleCase(fullName),
          email,
          role: "Farmer",
          companyName: normalizedCompanyName || null,
          timestamp,
        });
      } else if (accountType === "MilkSeller") {
        await setDoc(doc(firestore, "milkSellers", uid), {
          uid,
          fullName: titleCase(fullName),
          email,
          role: "MilkSeller",
          companyName: normalizedCompanyName || null,
          timestamp,
        });
      } else if (accountType === "Employee") {
        // Create employee document with pending status
        await setDoc(doc(firestore, "employees", uid), {
          uid,
          fullName: titleCase(fullName),
          email,
          role: "Employee",
          employeeStatus: "pending",
          requestedCompany: null,
          timestamp,
        });
      }

      setInfo("Signup successful! Redirecting...");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during signup.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white shadow-xl rounded-3xl p-8 sm:p-12 border border-gray-100">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white text-lg font-bold">
              N
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Create an account</h2>
            <p className="text-sm text-gray-500 mt-1">
              Choose how you'll use Neuracraft — Farmer, Company, Milk Seller, or Employee.
            </p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                required
                className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a strong password"
                required
                className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Account type</label>
              <select
                value={accountType}
                onChange={(e) => setAccountType(e.target.value)}
                required
                className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Select account type</option>
                <option value="Company">Company</option>
                <option value="Farmer">Farmer</option>
                <option value="MilkSeller">Milk Seller</option>
                <option value="Employee">Employee</option>
              </select>
            </div>

            {accountType === "Company" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Neuracraft"
                    required
                    className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Registration number (optional)
                  </label>
                  <input
                    type="text"
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    placeholder="e.g. NR-2025-001"
                    className="mt-1 block w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Terms and Conditions Checkbox for Company Accounts */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start">
                    <input
                      id="terms-checkbox"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="terms-checkbox" className="ml-3 text-sm text-gray-700">
                      I accept the{" "}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-blue-600 hover:text-blue-700 font-medium underline"
                      >
                        Terms and Conditions
                      </button>
                      {" "}and acknowledge that my data may be used to improve Neuracraft's AI models while maintaining privacy protections.
                    </label>
                  </div>
                  <p className="ml-7 mt-2 text-xs text-gray-600">
                    <strong>Note:</strong> You will receive a 30-day free trial. After the trial, your account will be billed monthly.
                  </p>
                </div>
              </>
            )}

            {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
            {info && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{info}</div>}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-xl text-white font-semibold transition ${
                loading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? "Creating account..." : "Create account"}
            </button>

            <div className="text-center text-sm text-gray-500 mt-4">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-blue-600 hover:underline"
              >
                Log in
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setShowTermsModal(false);
        }}
      />
    </div>
  );
}
